import React, { useCallback, useEffect, useState, useRef, useLayoutEffect } from 'react';
import { Button } from 'src/components/ui';
import useActionReducer from '../../util/useActionReducer';
import useElementSizeCheckHook from '../../util/useElementSizeCheckHook';
import Icon from 'src/components/Icon';

function JsonComponent({ value, onChange: propOnChange, maxHeight }) {
  const [state, dispatch] = useActionReducer(actions, {});
  const { jsonText, hasError, isPretty } = state;

  const textAreaRef = useRef();

  const setJsonText = useCallback(
    jsonText => {
      try {
        const value = JSON.parse(jsonText);
        dispatch.setJsonText({ jsonText, hasError: false });
        propOnChange(value);
      } catch (e) {
        dispatch.setJsonText({ jsonText, hasError: true });
      }
    },
    [propOnChange]
  );

  const onChange = useCallback(({ target: { value } }) => setJsonText(value), [setJsonText]);
  const onKeyDown = useCallback(
    e => {
      const { target, key } = e;

      if (key === '"') {
        const { selectionStart, selectionEnd, value } = target;
        if (selectionStart === selectionEnd) return;

        e.stopPropagation();
        e.preventDefault();

        const selectedText = value.substring(selectionStart, selectionEnd);

        document.execCommand('insertText', null, `"${selectedText}"`);
      }
    },
    [setJsonText]
  );

  useEffect(() => {
    dispatch.setValue({ value });
  }, [value]);

  const [height, setHeight] = useState('auto');

  useLayoutEffect(() => {
    textAreaRef.current.style.height = 'inherit';

    if (maxHeight) {
      textAreaRef.current.style.height = `max(min(${maxHeight}px, ${textAreaRef.current.scrollHeight}px), 40px)`;
    } else {
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
    }
  }, [jsonText]);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
      }}
    >
      <textarea
        className="w-full font-mono bg-gray-800 text-gray-400 p-4 rounded overflow-x-auto text-sm layer-base"
        ref={textAreaRef}
        style={{ height }}
        type="textarea"
        value={jsonText || ''}
        onChange={onChange}
        onKeyDown={onKeyDown}
        rows={1}
      />
      <div
        style={{
          position: 'absolute',
          top: '1px',
          right: '1px',
        }}
      >
        {hasError ? (
          <Icon icon="WarningCircle" style={{ fontSize: '2rem', color: 'red' }} />
        ) : !isPretty ? (
          <Button title="Make Pretty" onClick={() => dispatch.prettify({})}>
            <Icon icon="MagicWand" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}

const actions = {
  prettify(_, { jsonText }) {
    try {
      const prettyJsonText = JSON.stringify(JSON.parse(jsonText), null, 4);

      return {
        jsonText: prettyJsonText,
        prettyJsonText,
        isPretty: true,
      };
    } catch {
      return {
        jsonText,
      };
    }
  },
  setJsonText({ jsonText, hasError }) {
    let prettyJsonText = '';

    try {
      prettyJsonText = JSON.stringify(JSON.parse(jsonText), null, 4);
    } catch {
      // could not parse jsonText
    }

    return {
      jsonText,
      prettyJsonText,
      isPretty: prettyJsonText === jsonText,
      hasError,
    };
  },
  setValue({ value }, { jsonText }) {
    const valueJsonText = JSON.stringify(value, null, 4) || '';
    let prettyJsonText = '';

    try {
      prettyJsonText = JSON.stringify(JSON.parse(jsonText), null, 4);
    } catch {
      // could not parse jsonText
    }

    if (valueJsonText !== prettyJsonText) {
      return {
        jsonText: valueJsonText,
        prettyJsonText: valueJsonText,
        isPretty: true,
      };
    }

    return { jsonText };
  },
};

export default JsonComponent;
